import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
    ErrorStatusTypes,
    ErrorsStack,
    parseErrorMessage,
} from "components/common/errorsDeclarations";
import * as odpLegacyApi from "api/produzione/odpLegacyApi";
import { toast } from "react-toastify";
import { FiltriOdPLegacy } from "components/userPreference/userPreferenceSlice";
import {Ostacolo} from "../ostacoli/ostacoliSlice";

// ============================================
// COSTANTI
// ============================================

export const STATI_ODP: Record<number, string> = {
    0: "Da pianificare",
    1: "Pianificato",
    2: "Lanciato",
    3: "In avanzamento",
    4: "Terminato",
};

// ============================================
// INTERFACES
// ============================================

export interface FaseFrazioneMini {
    id:number,
    data_inizio_pianificata:string,
    quantita:number,
    attuale:boolean
}
export interface FaseLegacy {
    id?: number;
    id_riga?: number;
    id_riga_fase?: number;
    numero_fase?: number;
    codice_articolo?: string;
    unita_misura?: string;
    quantita?: number;
    codice_lavorazione?: string;
    descrizione?: string;
    data_inizio?: string;
    data_fine?: string;
    data_inizio_schedulata?: string;
    data_fine_schedulata?: string;
    stato?: number;
    stato_display?: string;
    tempo_attrezzaggio?: number;
    tempo_lavorazione?: number;
    ore_previste?: number;
    ore_lavorate?: number;
    ore_previste_residue?: number;
    ore_schedulate?: number;
    quantita_lavorata?: number;
    quantita_schedulata?: number;
    percentuale_avanzamento?: number;
    risorsa_principale?: string;
    risorsa_secondaria?: string;
    reparto?: number;
    reparto_display?: string;
    operatore?: number | null;
    operatore_display?: string | null;
    fasi_frazione?:[FaseFrazioneMini]
}

export interface FrazioneOdPMini {

    id:number,
    data_inizio_pianificata:string,
    attuale:boolean
}
export interface OdPLegacy {
    id?: number;
    id_documento?: number;
    esercizio?: string;
    numero_registrazione?: string;
    data_creazione?: string;
    tipo_documento?: number;
    codice_cliente_legacy?: number;
    nome_cliente?: string;
    codice_articolo?: string;
    unita_misura?: string;
    quantita?: number;
    quantita_lavorata?: number;
    quantita_residua?: number;
    percentuale_schedulazione?: number;
    data_inizio_pianificata?: string;
    data_fine_pianificata?: string;
    stato?: number;
    stato_display?: string;
    fasi?: FaseLegacy[];
    frazioni?: FrazioneOdPMini[];
    ostacoli?: Ostacolo[];
    ultimo_sync?:string
    errorsStack?: ErrorsStack;
}

export interface SeguiOdPLegacyResponse {
    odp: OdPLegacy[];
    errori: string[];
}

export interface OdPLegacyList {
    count: number;
    page: number;
    num_pages: number;
    next?: string;
    previous?: string;
    results: OdPLegacy[];
    errorsStack: ErrorsStack;
}

// ============================================
// STATO SLICE
// ============================================

export interface OdPLegacyState {
    odpLegacyList: OdPLegacyList;
    odpLegacyCorrenteId?: number; // SOLO ID, mai oggetto completo
    // NO filtri qui! Sono in userPreferenceSlice
}

const initialState: OdPLegacyState = {
    odpLegacyList: {
        count: 0,
        page: 0,
        num_pages: 0,
        next: undefined,
        previous: undefined,
        results: [],
        errorsStack: { status: ErrorStatusTypes.OK },
    },
    odpLegacyCorrenteId: undefined,
};

// ============================================
// ASYNC THUNKS
// ============================================

export const fetchOdPLegacy = createAsyncThunk(
    "odpLegacy/fetchOdPLegacy",
    async (parametri: FiltriOdPLegacy & { goto_id?: number } = {}) => {
        const { goto_id, ...filtri } = parametri;
        return await odpLegacyApi.fetchOdPLegacy(filtri, goto_id);
    }
);

export const getOdPLegacy = createAsyncThunk(
    "odpLegacy/getOdPLegacy",
    async (parametri: { odpLegacyId: number }) => {
        return await odpLegacyApi.getOdPLegacy(parametri.odpLegacyId);
    }
);

export const seguiOdPLegacy = createAsyncThunk<
    SeguiOdPLegacyResponse,
    { esercizio: number; tipo_documento: number; numero_registrazione: number }
>(
    "odpLegacy/seguiOdPLegacy",
    async (parametri) => {
        return await odpLegacyApi.seguiOdPLegacy(parametri.esercizio, parametri.tipo_documento, parametri.numero_registrazione);
    }
);
export const aggiornaOdPLegacy = createAsyncThunk(
    "odpLegacy/aggiornaOdPLegacy",
    async () => {
        return await odpLegacyApi.aggiornaOdPLegacy();
    }
);


// FaseLegacy è in massima parte read-only dal sistema legacy.
// Eccezione: l'operatore può essere assegnato in Claire dallo schedulatore
// quando eSolver non lo ha — il sync legacy non sovrascrive le scelte fatte qui.
export const saveFaseLegacyOperatore = createAsyncThunk(
    "odpLegacy/saveFaseLegacyOperatore",
    async (parametri: { odp_id: number; fase_id: number; operatore_id: number | null }) => {
        return await odpLegacyApi.saveFaseLegacyOperatore(
            parametri.odp_id,
            parametri.fase_id,
            parametri.operatore_id,
        );
    }
);

export const deleteOdPLegacy = createAsyncThunk(
    "odpLegacy/deleteOdPLegacy",
    async (parametri: { odpLegacyToDelete: OdPLegacy }) => {
        return await odpLegacyApi.deleteOdPLegacy(parametri.odpLegacyToDelete);
    }
);

// ============================================
// SLICE
// ============================================

export const odpLegacySlice = createSlice({
    name: "odpLegacy",
    initialState,
    reducers: {
        setOdPLegacyCorrenteId: (state, action: PayloadAction<number | undefined>) => {
            state.odpLegacyCorrenteId = action.payload;
        },
        resetOdPLegacy: (state) => {
            state.odpLegacyCorrenteId = undefined;
            state.odpLegacyList = initialState.odpLegacyList;
        },
    },
    extraReducers: (builder) => {
        // ============================================
        // FETCH (Lista)
        // ============================================
        builder.addCase(fetchOdPLegacy.pending, (state) => {
            state.odpLegacyList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(fetchOdPLegacy.fulfilled, (state, action) => {
            state.odpLegacyList = action.payload;
            state.odpLegacyList.results.sort(
                (a, b) => (a.percentuale_schedulazione ?? 0) - (b.percentuale_schedulazione ?? 0)
            );
            state.odpLegacyList.errorsStack = { status: ErrorStatusTypes.OK };
        });
        builder.addCase(fetchOdPLegacy.rejected, (state, action) => {
            state.odpLegacyList.errorsStack = parseErrorMessage(action.error);
        });

        // ============================================
        // GET (Singolo con fasi nestate)
        // ============================================
        builder.addCase(getOdPLegacy.pending, (state) => {
            state.odpLegacyList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(getOdPLegacy.fulfilled, (state, action) => {
            // Rimuove versione esistente
            state.odpLegacyList.results = state.odpLegacyList.results.filter(
                (odp) => odp.id !== action.payload.id
            );
            // Aggiunge versione aggiornata
            state.odpLegacyList.results.push(action.payload);
            // Ordina per percentuale_schedulazione crescente
            state.odpLegacyList.results.sort(
                (a, b) => (a.percentuale_schedulazione ?? 0) - (b.percentuale_schedulazione ?? 0)
            );

            state.odpLegacyCorrenteId = action.payload.id;
            state.odpLegacyList.errorsStack = { status: ErrorStatusTypes.OK };
        });
        builder.addCase(getOdPLegacy.rejected, (state, action) => {
            state.odpLegacyList.errorsStack = parseErrorMessage(action.error);
        });

        // ============================================
        // =====segui     ========
        // ============================================

        builder.addCase(seguiOdPLegacy.pending, (state) => {
            state.odpLegacyList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(seguiOdPLegacy.fulfilled, (state, action) => {
            const nuoviOdp = action.payload.odp;
            const nuoviIds = new Set(nuoviOdp.map((o) => o.id));
            state.odpLegacyList.results = state.odpLegacyList.results.filter(
                (odp) => !nuoviIds.has(odp.id)
            );
            state.odpLegacyList.results.unshift(...nuoviOdp);
            state.odpLegacyCorrenteId = nuoviOdp.length > 0 ? nuoviOdp[0].id : state.odpLegacyCorrenteId;
            state.odpLegacyList.errorsStack = { status: ErrorStatusTypes.OK };
        });
        builder.addCase(seguiOdPLegacy.rejected, (state, action) => {
            state.odpLegacyList.errorsStack = parseErrorMessage(action.error);
        });

        // ============================================
        // SAVE FASE LEGACY OPERATORE
        // ============================================
        builder.addCase(saveFaseLegacyOperatore.fulfilled, (state, action) => {
            const faseAggiornata: FaseLegacy = action.payload;
            const odp = state.odpLegacyList.results.find(
                (o) => o.id === action.meta.arg.odp_id
            );
            if (odp && odp.fasi) {
                odp.fasi = odp.fasi.map((f) =>
                    f.id === faseAggiornata.id ? { ...f, ...faseAggiornata } : f
                );
            }
        });
        builder.addCase(saveFaseLegacyOperatore.rejected, (state, action) => {
            state.odpLegacyList.errorsStack = parseErrorMessage(action.error);
            toast.error("Errore nell'assegnazione dell'operatore");
        });

        // ============================================
        // DELETE
        // ============================================
        builder.addCase(deleteOdPLegacy.pending, (state) => {
            state.odpLegacyList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(deleteOdPLegacy.fulfilled, (state, action) => {
            state.odpLegacyList.results = state.odpLegacyList.results.filter(
                (odp) => odp.id !== action.meta.arg.odpLegacyToDelete.id
            );
            state.odpLegacyList.count--;
            state.odpLegacyList.errorsStack = { status: ErrorStatusTypes.OK };

            toast.success("OdP Legacy eliminato con successo");
        });
        builder.addCase(deleteOdPLegacy.rejected, (state, action) => {
            state.odpLegacyList.errorsStack = parseErrorMessage(action.error);
            toast.error("Errore nell'eliminazione dell'OdP");
        });
    },
});

// ============================================
// EXPORTS
// ============================================

export const {
    setOdPLegacyCorrenteId,
    resetOdPLegacy,
} = odpLegacySlice.actions;

export const odpLegacySliceReducer = odpLegacySlice.reducer;